import React, { useMemo } from 'react';
import { Chart, ChartSeries, ChartSeriesItem, ChartCategoryAxis, ChartCategoryAxisItem, ChartValueAxis, ChartValueAxisItem, ChartTooltip, ChartTitle, ChartCategoryAxisTitle, ChartSeriesLabels } from '@progress/kendo-react-charts';
import { ITicketProps } from '../../../../../../types/Ticket/ITicket';
import { Link } from 'react-router-dom';
import { TicketApi } from '../../../../../../redux/api/TicketApi';
import { useReduxSelector } from '../../../../../../redux/hooks';
import { IAction } from '../../../../../../types/Action/IAction';
import { Avatar } from '@progress/kendo-react-layout';
import { SystemApi } from '../../../../../../redux/api/SystemApi';

const TicketStoryLine3: React.FC<ITicketProps> = (ticket) => {
  const filter = useReduxSelector(state => state.filter);
  const { data: calendar, error, isFetching: fetch } = SystemApi.useGetWorkHoursQuery({ slaId: ticket?.SLA_Id, start: ticket.Open_Dt, end: ((ticket.Open_Status === 0 && ticket.Close_Dt) || null) });
  const { data: actions, refetch, isFetching } = TicketApi.useGetActionsQuery({ ticketId: Number(ticket?.ID), take: 100, skip: 0, ...filter }, { skip: ticket?.ID === undefined });
  const { data: stateHistory } = TicketApi.useGetStateHistoryQuery((ticket?.ID));
  const [view, setView] = React.useState({ text: 'Daily', value: 0 });

  const getKeyFromDate = (date: Date) => {
    var date = new Date(date + 'Z');
    //Hourly
    if (view?.value == 1) {
      date.setMinutes(0, 0, 0);
      return date.toLocaleDateString() + ' ' + date.toLocaleString([], { hour: '2-digit', minute: '2-digit' });
    }
    return new Date(date + 'Z').toLocaleDateString();
  }

  //console.warn('DUDE', calendar, error, fetch);

  const chartData = calendar?.reduce((previousActions: IChartData[], cal) => {
    const date = getKeyFromDate(cal.Day);
    //const previousDate = previousAction.date;
    var state = stateHistory?.slice().sort((a, b) => new Date(b?.Created_Dt).getTime() - new Date(a?.Created_Dt).getTime())
    var lastState = state.find(s => new Date(s.Created_Dt + 'Z') <= new Date(date));
    var theColor = '';
    var stateChangeAction = null as IAction
    if (lastState) {
      if (lastState.New_State === 'Closed' || lastState.New_State?.indexOf('Late') > -1) {
        theColor = '#ff0000';//Closed (Red)
      } else if (lastState.New_State?.indexOf('Risk') > -1) {
        theColor = '#ecbe4e'; //Yello
      } else if (lastState.New_State === 'Current') {
        theColor = '#697cc1';//Blue
      } else if (lastState.New_State === 'New') {
        theColor = '#73a913';//Green
      }
      stateChangeAction = { Action_Type_Name: 'State Change', Action_Notes: 'Ticket state changed to ' + lastState.New_State, Action_Dt: new Date(lastState.Created_Dt), Is_System_Action: true } as IAction;
    } else {
      theColor = '#73a913';//Green
    }

    //Any actions that happened on this day
    var dayActions = actions?.Items?.filter(a => new Date(a.Action_Dt + 'Z').toLocaleDateString() === date);

    //Any state changes that happened on this day
    var stateChanges = stateHistory?.filter(s => new Date(s.Created_Dt + 'Z').toLocaleDateString() === date).map(sh => { return { Action_Dt: new Date(sh.Created_Dt), Action_Notes: 'Ticket state changed to ' + sh.New_State, Is_System_Action: true } as IAction });
    dayActions = dayActions.concat(stateChanges);
    //if (stateChangeAction)
    //dayActions = dayActions.concat(stateChangeAction);

    var newDayAction = { date: date, daysOpen: 1, actions: dayActions, color: theColor } as IChartData;

    if (previousActions?.find(p => p.date === date))
      previousActions.find(p => p.date === date).actions.push({} as IAction)
    else {
      if (previousActions) {
        //previousActions.push(newDayAction);

        //If there are no actions just increase the daysOpen by 1 day
        if (previousActions.length && !previousActions[previousActions.length - 1]?.actions?.length
          && (newDayAction.actions?.length === 0)) {
          previousActions[previousActions.length - 1].daysOpen++;
          previousActions[previousActions.length - 1].date = date;
        } else {
          previousActions.push(newDayAction);
        }
      }
      else
        previousActions = [newDayAction];
    }

    //console.warn('previousActions', previousActions)
    return previousActions;
  }, [] as IChartData[]);


  const chartData2 = stateHistory && actions?.Items?.slice().sort((a, b) => new Date(a.Action_Dt).getTime() - new Date(b.Action_Dt).getTime()).filter(a => a.ID).reduce((previousActions: IChartData[], action) => {
    //const date = new Date(action.Action_Dt + 'Z').toDateString();
    const date = getKeyFromDate(action.Action_Dt);
    //const previousDate = previousAction.date;
    var state = stateHistory?.slice().sort((a, b) => new Date(b?.Created_Dt).getTime() - new Date(a?.Created_Dt).getTime())
    var lastState = state.find(s => new Date(s.Created_Dt + 'Z') <= new Date(date));
    var theColor = '';
    if (lastState) {
      if (lastState.New_State === 'Closed' || lastState.New_State?.indexOf('Late') > -1) {
        theColor = '#ff0000';//Closed (Red)
      } else if (lastState.New_State?.indexOf('Risk') > -1) {
        theColor = '#ecbe4e'; //Yello
      } else if (lastState.New_State === 'Current') {
        theColor = '#697cc1';//Blue
      } else if (lastState.New_State === 'New') {
        theColor = '#73a913';//Green
      }
    } else {
      theColor = '#73a913';//Green
    }
    if (previousActions?.find(p => p.date === date))
      previousActions.find(p => p.date === date).actions.push(action)
    else {
      var newDayAction = { date: date, daysOpen: 1, actions: [action], color: theColor } as IChartData;
      if (previousActions)
        previousActions.push(newDayAction);
      else
        previousActions = [newDayAction];
    }

    return previousActions;
  }, [] as IChartData[]);

  //Add ticket created and closed events
  if (chartData) {
    var key = getKeyFromDate(new Date(ticket.Open_Dt))
    var createdAction = [{ Action_Type_Name: 'Created', Action_Notes: 'Created', Action_Dt: new Date(ticket.Open_Dt), Is_System_Action: true }] as IAction[];
    //insert at the begining
    chartData.unshift({ date: key, label: 'Created', daysOpen: 1, actions: [], color: '#73a913' });
  }

  const formatAction = (action: IAction) => {
    return action.Is_System_Action
      ? <div className='p-2'> <p><Avatar ><img src='images/Host_Images/System_Action/Gears.png'></img></Avatar>{action.Staff_Name} {new Date(action.Action_Dt + 'Z').toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - <div className='d-inline' dangerouslySetInnerHTML={{ __html: action.HTML_Notes || action.Action_Notes }}></div></p></div>
      : <div className='p-2'><p><Avatar ><img src={action.Profile_Image_Url}></img></Avatar>{action.Staff_Name} {new Date(action.Action_Dt + 'Z').toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - <div className='d-inline' dangerouslySetInnerHTML={{ __html: action.HTML_Notes || action.Action_Notes }}></div></p></div>
  }

  //Calculate the label for the chart series
  chartData?.forEach((item, index) => {
    if (!item.label) {
      item.label = item.actions?.length ? (item.actions?.length.toString() + ' action/s') : '+' + item.daysOpen + ' days';

      //show the status change if thats the only action
      if (item.actions?.length === 1 && item.actions[0].Action_Type_Name === 'State Change')
        item.label = item.actions[0].Action_Notes;
    }
  });


  return (<>
    {/* stateHistory = {stateHistory?.map((state, index) => (<div>{state.Created_Dt} - {state.New_State}</div>))} */}
    {/* chartData = {JSON.stringify(chartData)} */}
    {/* <div>calendar = {calendar?.length}</div>
    <div>stateHistory = {stateHistory.map(s => (<>{s.Created_Dt} - {s.New_State}</>))}</div> */}
    <Chart className='w-100 mw-400px' style={{ height: '100vh' }} zoomable={false}>
      <ChartValueAxis>
        <ChartValueAxisItem title={{ text: 'Days Open' }} />
      </ChartValueAxis>
      <ChartSeries>
        {chartData?.map((item, index) => (
          <ChartSeriesItem type="column" stack={{ type: 'normal' }} data={[item]} noteTextField='days' field='daysOpen' color={item.color} >
            <ChartSeriesLabels content={() => item.label} position='insideEnd' background='none' color='white' font='13px' />
          </ChartSeriesItem>
        ))}

      </ChartSeries>
      <ChartTooltip render={props => (
        <div>
          <strong>{props.point.dataItem.date}</strong>: {props.point.value} days
          {props.point.dataItem.actions?.map((action, index) => (
            <div>{new Date(action.Action_Dt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}:
              {action.Staff_Name ? <Link className='nav-link' key={index} to={`/ticket/${ticket.ID}/action/${action.ID}`}> Action by {action.Staff_Name}</Link>
                : <div className='nav-link' key={index}>{action.Action_Notes}</div>}
            </div>
          ))}

        </div>
      )} />
    </Chart>   </>
  );
};

export default TicketStoryLine3;

export interface IChartData { date: string, label: string, daysOpen: number, actions: IAction[], color: string, showActions?: boolean }